import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getCmsDocument } from "@/api/cms";
import { CmsBlocks } from "./cms-blocks";

const DOCS_BASE = "/docs";

/**
 * Docs — a root section (not Settings) rendered entirely from CMS content in
 * the base table. Adding or editing a page is a data change, not a deploy.
 */
export function DocsPage() {
  const { slug = "" } = useParams<{ slug?: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ["cms", "help", slug],
    queryFn: () => getCmsDocument(slug),
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <div className="rounded-lg border border-border bg-card p-6">
          <p className="text-sm text-muted-foreground">
            That page could not be found.
          </p>
          <Link
            to={DOCS_BASE}
            className="mt-3 inline-flex text-sm font-medium text-brand hover:underline"
          >
            Back to docs
          </Link>
        </div>
      </div>
    );
  }

  const { document: doc, index } = data;

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">{doc.title}</h1>
        {doc.description && (
          <p className="mt-1 text-muted-foreground">{doc.description}</p>
        )}
      </header>

      <nav className="mb-6 flex flex-wrap gap-2">
        {index.map((entry) => {
          const active = entry.slug === doc.slug;
          const href = entry.slug ? `${DOCS_BASE}/${entry.slug}` : DOCS_BASE;
          return (
            <Link
              key={entry.documentId}
              to={href}
              className={
                active
                  ? "rounded-md bg-brand-soft px-3 py-1.5 text-sm font-medium text-brand"
                  : "rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-surface-hover hover:text-foreground"
              }
            >
              {entry.label ?? entry.title}
            </Link>
          );
        })}
      </nav>

      <CmsBlocks blocks={doc.body} index={index} basePath={DOCS_BASE} />
    </div>
  );
}
